import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Droplets, Flame, ClipboardCheck } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink } from "@/components/FiNav";
import { fiKosteusFaq, fiKosteusSections } from "@/data/fi-kosteus";

const TITLE = "Kosteusmittaus ja kaasutarkastus asuntoautoon";
const DESC =
  "Näin mittaamme asuntoauton kosteuden: piikkimittari, raja-arvot ja mittauspisteet. Kaasutarkastus ennen luovutusta. Palvelemme suomeksi.";
const URL = "https://www.akhusbilfritid.se/fi/kosteusmittaus";

const POINTS = [
  { icon: <Droplets size={18} />, t: "Alle 15 % kuiva", d: "Piikkimittari rakenteen läpi, kymmeniä mittauspisteitä." },
  { icon: <Flame size={18} />, t: "Kaasu tarkastettu", d: "Letkut, säädin, liitokset ja laitteet ennen luovutusta." },
  { icon: <ClipboardCheck size={18} />, t: "Lukemat kirjallisena", d: "Numerot pisteittäin — ei pelkkä hyväksymismerkintä." },
];

export const Route = createFileRoute("/fi/kosteusmittaus")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fiKosteusFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Kosteusmittaus ja kaasutarkastus – AK Husbil & Fritid, Kalix" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "alternate", hrefLang: "fi-FI", href: URL }],
    scripts: [
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://www.akhusbilfritid.se/fi" },
            { "@type": "ListItem", position: 2, name: "Kosteusmittaus ja kaasutarkastus", item: URL },
          ],
        }),
      },
    ],
  }),
  component: FiKosteusPage,
});

function FiKosteusPage() {
  return (
    <>
      <PageHeader
        label="Tarkastus · Kalix, 90 km Torniosta"
        title={
          <>
            Kosteusmittaus ja <span className="font-italic-display text-accent">kaasutarkastus</span>.
          </>
        }
        sub="Jokainen meiltä lähtevä asuntoauto ja asuntovaunu mitataan ja kaasulaitteet tarkastetaan ennen luovutusta. Saat lukemat kirjallisena pisteittäin — myös ne, jotka eivät ole täydellisiä."
      />

      <FiNav />

      <section className="container-edge py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {POINTS.map((p) => (
            <div key={p.t} className="rounded-3xl border border-border bg-card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">{p.icon}</span>
              <h2 className="mt-4 font-display text-xl">{p.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <HubEditorial
        eyebrow="Mittauskäytäntömme"
        sections={fiKosteusSections}
        faq={fiKosteusFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <section className="container-edge pb-14">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
          <p className="eyebrow">Seuraava askel</p>
          <h2 className="mt-3 font-display text-2xl">Varaa mittausaika tai katso varasto</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Mittaamme myös aiemmin ostetun ajoneuvon, vaikka se olisi ostettu muualta. Soita niin sovitaan aika
            — palvelemme suomeksi.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/fi/yhteystiedot"
              className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Varaa aika <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/asuntoautot"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Asuntoautot <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/ostajan-opas"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Ostajan opas <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <FiSwedishLink to="/erfarenheter" label="Fukt- & gasolrapporter på svenska" />
    </>
  );
}
